import { useEffect, useState } from "react";
import BasicCheckbox from "../../../components/basicCheckbox/basicCheckbox";
import BasicInput from "../../../components/inputs/basicInput/basicInput";
import BasicPanel from "../../../components/panels/basicPanel/basicPanel";
import { useInvoiceContext } from "../createInvoiceContext";
import { calculateInvoiceTotals } from "./utils/invoiceCalc";

import "./calculationInvoice.css";
import { Panel } from "rsuite";
import { getCurrencySymbol } from "../../../utils/currencyHelper";
import { getCurrencyRate } from "../../../api/currency";
import { useTranslation } from "react-i18next";

const CARET_SVG =
    <svg width="14" height="9" viewBox="0 0 14 9" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M0.298162 8.13654C-0.0659236 7.78507 -0.0659236 7.21522 0.298163 6.86375L6.51351 0.863751C6.8776 0.512278 7.4679 0.512278 7.83198 0.863751L13.702 6.53042C14.0661 6.88189 14.0661 7.45174 13.702 7.80321C13.3379 8.15468 12.7476 8.15468 12.3836 7.80321L7.17275 2.77294L1.61664 8.13654C1.25255 8.48801 0.66225 8.48801 0.298162 8.13654Z" fill="#17C653"/>
    </svg>

const LabelWithSubLabel = ({ label, subLabel }) => {
    return (
        <div className={`labelWithSubLabel`} style={{width: "max-content"}}>
            <div className="paragraph-14R" style={{color: "var(--Gray-800)"}}>{label}</div>
            <div className="paragraph-10R">{subLabel}</div>
        </div>
    )
}

const LabelWithValueAndKey = ({ label, subLabel, value }) => {
    return (
        <div className={`labelWithValueAndKey`}>
            <LabelWithSubLabel label={label} subLabel={subLabel} />
            <div className="paragraph-14R" style={{
                textWrap: "nowrap",
            }}>{value}</div>
        </div>
    )
}


function sanitizeInput(value) {
    return value.replace(/[^0-9.,]/g, '');
  }

  function fixDecimal(value) {
    let result = '';
    let decimalUsed = false;
  
    for (let i = 0; i < value.length; i++) {
      const ch = value[i];
      if (ch === '.' || ch === ',') {
        if (!decimalUsed) {
          // First decimal separator becomes '.' 
          decimalUsed = true;
          result += '.';
        }
        // If we already used a decimal separator, skip additional ones
      } else {
        result += ch;
      }
    }
  
    return result;
  }
  
  /**
   * A "fully typed" float must:
   *   - Not be empty
   *   - Contain digits
   *   - Optionally have one decimal point
   *   - Not end in decimal point
   * Examples of "fully typed": "5", "5.0", "123.456"
   * Examples of partial: "5.", "", ".5"
   */
  function isFullyTypedFloat(value) {
    if (!value) return false;
    if (value.endsWith('.')) return false; // trailing decimal -> partial
    // Must match digits, optional decimal, digits
    return /^\d+(\.\d+)?$/.test(value);
  }
  
  /**
   * Main function:
   *   - Remove invalid chars
   *   - Normalize decimal
   *   - If fully typed float, parse & return as number
   *   - Otherwise keep partial string
   */
  function getFloatValue(value) {
    // 1. Clean any invalid chars
    let cleaned = sanitizeInput(value);
  
    // 2. Normalize decimal separators to a single '.'
    cleaned = fixDecimal(cleaned);
  
    // 3. If it's a fully typed float, parse it
    if (isFullyTypedFloat(cleaned)) {
      const num = parseFloat(cleaned);
      // If you want to disallow 0 or negative, clamp to 0 or do something else:
      return num > 0 ? num : 0;
    }
  
    // 4. Partial input => return the (cleaned) string so user can keep typing
    return cleaned;
  }

const GoodPanel = ({ good, currencySymbol }) => {
    const { t } = useTranslation('basic');
    
    return (
        <Panel bodyFill collapsible
        caretAs={() => <span className="panelCaret">{CARET_SVG}</span>}
        header={
            <LabelWithSubLabel label={<span className="paragraph-14M">{good.name}</span>} subLabel={good.count + " " + t("pcs")} />
        }
        className="goodPanel">
            <div className="goodPanelRows">
                    <div className="goodPanelRowItem">
                        <span className="paragraph-14R" style={{color: "var(--Gray-600)"}}>
                            {t("insurance_per_unit")}
                        </span>
                        <span className="paragraph-14R">
                            {good.insurance + " " + currencySymbol}
                        </span>
                    </div>

                    <div className="goodPanelRowItem">
                        <span className="paragraph-14R" style={{color: "var(--Gray-600)"}}>
                            {t("additional_charge_cod")}
                        </span>
                        <span className="paragraph-14R">
                            {good.price ? (good.price + " " + currencySymbol) : t("no")}
                        </span>
                    </div>
            </div>
        </Panel>
    )
}

const PlacePanel = ({ place, currencySymbol, t }) => {
    return (
        <BasicPanel className="placePanel" bodyFill header={<div>{t("place_word")} {place.number}</div>}>
            {place.goods.map((good, index) => <GoodPanel key={index} good={good} currencySymbol={currencySymbol} />)}
        </BasicPanel>
    )
}

export default function InvoiceCalculation() {
    const { t } = useTranslation('basic');
    const { invoiceData, setInvoiceData } = useInvoiceContext();
    const [refresh, setRefresh] = useState(0);
    const currencySymbol = getCurrencySymbol(invoiceData?.user_info);
    const [currencyRate, setCurrencyRate] = useState(null); // KZT rate relative to RUB
    
    // Load currency rate for conversion (always load for proper conversions)
    useEffect(() => {
        const loadCurrencyRate = async () => {
            try {
                const rate = await getCurrencyRate('KZT');
                const rateValue = rate.value; // e.g., 6.0 (1 RUB = 6 KZT)
                setCurrencyRate(rateValue);
                // Store rate in invoiceData for use in arrangeInvoiceParams
                setInvoiceData(oldData => ({...oldData, currencyRate: rateValue}));
            } catch (error) {
                console.error('Failed to load currency rate:', error);
                const fallbackRate = 6.0;
                setCurrencyRate(fallbackRate);
                setInvoiceData(oldData => ({...oldData, currencyRate: fallbackRate}));
            }
        };
        
        loadCurrencyRate();
    }, []);

    useEffect(() => {
        console.log("invoiceData", invoiceData)
    }, [refresh]);

    // calculations
    useEffect(() => {
        let ESTR = 0, EOSP = 0;
        invoiceData.places.forEach(place => {
            place.goods.forEach(good => {
                ESTR += good.insurance * good.count;
                EOSP += good.price * good.count;
            });
        });

        const Nds  = (invoiceData?.user_info?.contract_vat || 1) - 1;
        const r    = invoiceData?.user_info?.contract_type == 2 ? 0.05 : 0.03;
        const Astr = (invoiceData?.user_info?.insurance_procent || 0) / 10000;

        // delivery_sum — NET тариф из API (без НДС)
        // additions_sum — GROSS доп. услуги (НДС уже добавлен на шаге 3)
        const YTAR_NET    = invoiceData.delivery_sum || 0;
        const XDOP_GROSS  = invoiceData.additions_sum || 0;
        const SSTR        = Astr * ESTR;
        const SSERV_NET   = YTAR_NET + (Nds > 0 ? XDOP_GROSS / (1 + Nds) : XDOP_GROSS) + SSTR;
        const SSERV_VAT   = SSERV_NET * Nds;
        const SSERV_GROSS = SSERV_NET + SSERV_VAT;

        const hand_sum_raw = invoiceData.hand_sum;
        const hand_sum = hand_sum_raw ? parseFloat(hand_sum_raw) : null;

        const result = calculateInvoiceTotals({
            SSERV_GROSS,
            EOSP,
            r,
            Nds,
            payGoods:                invoiceData.payment_on_receipt,
            payDelivery:             invoiceData.delivery_pay,
            recipientPaysCommission: invoiceData.commission,
            hand_sum,
        });

        console.log(
`[invoiceCalc] ── Входные значения ──────────────────
YTAR_NET   = ${YTAR_NET}
XDOP_GROSS = ${XDOP_GROSS}
ESTR       = ${ESTR}
EOSP       = ${EOSP}
Astr       = ${Astr}
Nds        = ${Nds}
r          = ${r}
payGoods   = ${invoiceData.payment_on_receipt}
payDelivery= ${invoiceData.delivery_pay}
commission = ${invoiceData.commission}

[invoiceCalc] ── Блок услуг CDEK ───────────────────
SSTR         = Astr × ESTR         = ${SSTR}
SSERV_NET    = YTAR + XDOP_net + SSTR = ${SSERV_NET}
SSERV_VAT    = SSERV_NET × Nds     = ${SSERV_VAT}
SSERV_GROSS  = SSERV_NET + VAT     = ${SSERV_GROSS}

[invoiceCalc] ── База комиссии ─────────────────────
Goods part   = ${invoiceData.payment_on_receipt ? EOSP : 0}
Delivery part= ${invoiceData.delivery_pay ? SSERV_GROSS : 0}
RBASE        = ${result.RBASE}

[invoiceCalc] ── Комиссия ──────────────────────────
XAg exact    = ${result.XAg}
XAgVAT exact = ${result.XAgVAT}
XAgTOTAL exact = ${result.XAgTOTAL}

[invoiceCalc] ── Финальные значения ────────────────
ADOPSBOR exact = ${result.ADOPSBOR}
AitogP exact   = ${result.AitogP}
AitogO exact   = ${result.AitogO}
ADOPSBOR display = ${result.ADOPSBOR_DISPLAY}
AitogP display   = ${result.AitogP_DISPLAY}
AitogO display   = ${result.AitogO_DISPLAY}`
        );

        // Additional fee for payment on receipt based on route and contract type.
        // Rules per business diagram (sender can be any EAEU country):
        //   RF → KZ, KZ contract only: +250 KZT
        //   KZ → RF, KZ contract only: +50 RUB (sent as KZT so backend KZT→RUB gives 50 RUB)
        //   All other EAEU routes (EAEU→KZ, EAEU→RF, KZ→KZ, etc.): no extra fee
        let additional_fee = 0;
        let additional_fee_display = 0;

        if (invoiceData.payment_on_receipt && currencyRate) {
            const cityFrom = invoiceData.cityFromCountryCode;
            const cityTo = invoiceData.cityToCountryCode;
            const isKZContract = invoiceData?.user_info?.is_kz;

            if (cityFrom === 1 && cityTo === 48 && isKZContract) {
                // RF → KZ, KZ contract: +250 KZT
                additional_fee = 250;
                additional_fee_display = 250;
            } else if (cityFrom === 48 && cityTo === 1 && isKZContract) {
                // KZ → RF, KZ contract: +50 RUB expressed in KZT
                additional_fee = Math.ceil(50 * currencyRate);
                additional_fee_display = Math.ceil(50 * currencyRate);
            }
            // All other routes: no additional fee
        }

        // additional_fee всегда целое (0, 250, или Math.ceil(50 * rate))
        // ceil(X + N) = ceil(X) + N для целого N
        const AitogP_with_fee = result.AitogP_DISPLAY + additional_fee;

        setInvoiceData(oldData => ({
            ...oldData,
            delivery_pay_amount:         AitogP_with_fee,
            delivery_pay_amount_for_api: AitogP_with_fee,
            contract_pay:                result.AitogO_DISPLAY,
            additional_sum:              invoiceData.payment_on_receipt
                                           ? result.ADOPSBOR_DISPLAY + additional_fee
                                           : 0,
            additional_fee,
            additional_fee_display,
        }));

        setRefresh(refresh => refresh + 1);
    }, [invoiceData.commission, invoiceData.delivery_pay, invoiceData.hand_sum, invoiceData.payment_on_receipt, invoiceData.cityFromCountryCode, invoiceData.cityToCountryCode, invoiceData?.user_info?.is_kz, invoiceData?.user_info?.contract_vat, invoiceData?.user_info?.insurance_procent, invoiceData.delivery_sum, invoiceData.additions_sum, currencyRate]);

    return (
        <div className="invoiceCalculationWrapper">
            <BasicPanel header={t("calculation")} className="invoiceCalculation">
                <BasicCheckbox
                    disabled={!invoiceData.payment_on_receipt}
                    label={t("commission_from_receiver")}
                    checked={invoiceData.commission}
                    onChange={(value) => {
                        setInvoiceData(oldData => {
                            const newData = { ...oldData };
                            newData.commission = value;
                            return newData;
                        });

                        setRefresh(refresh => refresh + 1);
                    }}
                />
                
                <BasicCheckbox
                    disabled={!invoiceData.payment_on_receipt}
                    label={t("delivery_from_receiver")}
                    size="lg"
                    checked={invoiceData.delivery_pay}
                    onChange={(value) => {
                        setInvoiceData(oldData => {
                            const newData = { ...oldData };
                            newData.delivery_pay = value;
                            return newData;
                        });

                        setRefresh(refresh => refresh + 1);
                    }}
                />
            </BasicPanel>

            <BasicPanel>
                    <div className="calculationInfo">
                        <LabelWithValueAndKey
                            label={t("payment_from_receiver")}
                            subLabel={t("payment_from_receiver_hint")} 
                            value={(invoiceData.delivery_pay_amount || 0) + " " + currencySymbol} />

                        <LabelWithValueAndKey
                            label={t("payment_by_contract")}
                            subLabel={t("payment_by_contract_hint")}
                            value={(invoiceData.contract_pay || 0) + " " + currencySymbol} />
                        
                        <BasicInput
                            disabled={!invoiceData.payment_on_receipt}
                            label={t("additional_collection_from_receiver")}
                            placeholder={invoiceData.additional_sum}
                            helpTextHighlight={!invoiceData.payment_on_receipt}
                            helpText={t("additional_collection_hint")}
                            addonAfter={currencySymbol}
                            value={invoiceData.hand_sum || ""}
                            onChange={(value) => {
                                if (!invoiceData.payment_on_receipt) return;
                                
                                setInvoiceData(oldData => {
                                    const newData = { ...oldData };
                                    newData.hand_sum = getFloatValue(value);
                                    return newData;
                                })
                            }} />
                    </div>
            </BasicPanel>

            {
                invoiceData.places.map((place, index) => <PlacePanel key={index} place={{...place, number: index + 1}} currencySymbol={currencySymbol} t={t} />)
            }
        </div>
    )
}